import { Link } from "react-router-dom";
import {
  MessageSquare,
  Sparkles,
  FileUp,
  FileText,
  Gavel,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const detailedSteps = [
  {
    n: "01",
    icon: MessageSquare,
    title: "Tell Lexi what happened",
    desc: "No forms. No jargon. Just describe your situation the way you'd explain it to a friend. Lexi listens first, then identifies what kind of legal matter you're describing.",
  },
  {
    n: "02",
    icon: Sparkles,
    title: "Lexi asks the right questions",
    desc: "Based on your case type, Lexi asks targeted, attorney-relevant follow-ups — dates, parties, damages, evidence, jurisdiction — without overwhelming you.",
  },
  {
    n: "03",
    icon: FileUp,
    title: "Upload supporting documents",
    desc: "Drop in photos, contracts, medical records, police reports, emails, or videos. PDF, JPG, PNG, DOCX, XLSX, and MP4 are all supported.",
  },
  {
    n: "04",
    icon: FileText,
    title: "Get an organized case summary",
    desc: "Lexi generates an attorney-ready file with executive summary, timeline, key facts, damages, parties, urgency and completeness scores.",
  },
  {
    n: "05",
    icon: Gavel,
    title: "Matched to qualified attorneys",
    desc: "Approved attorneys subscribed to LexIntake review cases that match their practice areas and licensed states. You hear from people equipped to help.",
  },
];

export default function HowItWorksPage() {
  return (
    <div data-testid="how-it-works-page" className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#C5A059] mb-4">
            How It Works
          </p>
          <h1 className="font-heading text-5xl lg:text-6xl font-bold tracking-tight text-[#0A192F] leading-[1.05]">
            From your story to an{" "}
            <span className="font-serif-display italic font-normal">
              attorney-ready file.
            </span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            LexIntake replaces clunky intake forms with a conversation. Tell
            Lexi what happened — once — and we&apos;ll organize everything an
            attorney needs to evaluate your case.
          </p>
        </div>

        <div className="mt-16 space-y-6">
          {detailedSteps.map((s, i) => (
            <div
              key={s.n}
              data-testid={`detailed-step-${s.n}`}
              className="grid lg:grid-cols-12 gap-6 items-start bg-white border border-slate-200 rounded-xl p-8"
            >
              <div className="lg:col-span-1">
                <span className="font-heading text-3xl font-bold text-[#C5A059]">
                  {s.n}
                </span>
              </div>
              <div className="lg:col-span-2">
                <span className="w-12 h-12 rounded-lg bg-[#0A192F] flex items-center justify-center">
                  <s.icon className="w-6 h-6 text-[#C5A059]" />
                </span>
              </div>
              <div className="lg:col-span-9">
                <h2 className="font-heading text-2xl font-semibold text-[#0A192F]">
                  {s.title}
                </h2>
                <p className="mt-3 text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 p-8 lg:p-12 bg-[#0A192F] rounded-2xl text-white grid lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-5 h-5 text-[#C5A059]" />
              <span className="text-xs tracking-[0.22em] uppercase font-semibold text-[#C5A059]">
                Important Notice
              </span>
            </div>
            <h3 className="font-heading text-2xl font-semibold">
              LexIntake is not a law firm.
            </h3>
            <p className="mt-3 text-slate-300 leading-relaxed">
              We do not provide legal advice. No attorney-client relationship
              is formed by using this service. Information you submit is used
              for legal intake and case organization purposes. Please consult
              a licensed attorney for legal advice.
            </p>
          </div>
          <div className="lg:col-span-4 flex justify-start lg:justify-end">
            <Link to="/intake">
              <Button
                data-testid="how-it-works-cta-btn"
                className="bg-[#C5A059] hover:bg-[#D4AF37] text-[#0A192F] rounded-lg px-6 py-5 font-semibold"
              >
                Start Free Case Review
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
